import type { GameCode } from '../../entities/room/types';
import { betNetAmount } from '../game/betOutcomeText.ts';
import { rouletteColor } from '../game/winPreview.ts';

/**
 * The settled card, as a model. Built only from what the backend settled —
 * the PM ruled out any locally computed outcome (spec, Product decision 2).
 */
export const WIN_AUTO_CLOSE_MS = 15_000;
export const LOSS_AUTO_CLOSE_MS = 8_000;
const MAX_ROWS_COLLAPSED = 5;
const GAME_ORDER: GameCode[] = ['lastdigit', 'sicbo', 'roulette'];

export type CardBet = {
  id: string;
  game: string;
  tab: string;
  pick: string;
  amount: number;
  currency: string;
  result: 'pending' | 'win' | 'lose' | 'cancelled';
  payout: number;
};

export type CardDraw = { lastdigit?: number; sicbo?: number[]; roulette?: number };

export type DrawTag = { key: 'big' | 'small' | 'odd' | 'even' | 'red' | 'black' | 'green' | 'triple' } | { text: string };

export type HeroDraw =
  | { game: 'lastdigit'; digit: number; tags: DrawTag[] }
  | { game: 'sicbo'; dice: number[]; sum: number; tags: DrawTag[] }
  | { game: 'roulette'; pocket: number; tone: 'red' | 'black' | 'green'; tags: DrawTag[] };

export type CelebrationTier = 'none' | 'small' | 'win' | 'mega';

export type ResultRow = {
  id: string;
  game: string;
  tab: string;
  pick: string;
  currency: string;
  result: CardBet['result'];
  net: number;
};

export type ResultCardView = {
  layout: 'win' | 'loss';
  breakEven: boolean;
  /** Signed net per currency, fiat first. */
  nets: Array<{ currency: string; net: number }>;
  netByCurrency: Record<string, number>;
  hero: HeroDraw | null;
  /** The games the player did not bet, in lastdigit → sicbo → roulette order. */
  others: HeroDraw[];
  rows: ResultRow[];
  hiddenRows: number;
  celebration: CelebrationTier;
  autoCloseMs: number;
};

export function heroDrawFor(game: GameCode, draw: CardDraw | null | undefined): HeroDraw | null {
  if (!draw) return null;
  if (game === 'lastdigit') {
    const digit = Number(draw.lastdigit);
    if (!Number.isFinite(digit) || draw.lastdigit == null) return null;
    return { game, digit, tags: [{ key: digit >= 5 ? 'big' : 'small' }, { key: digit % 2 === 0 ? 'even' : 'odd' }] };
  }
  if (game === 'sicbo') {
    const dice = (draw.sicbo || []).map(Number);
    if (dice.length !== 3 || dice.some((value) => !Number.isFinite(value))) return null;
    const sum = dice.reduce((total, value) => total + value, 0);
    const triple = dice.every((value) => value === dice[0]);
    return { game, dice, sum, tags: triple ? [{ key: 'triple' }] : [{ key: sum >= 11 ? 'big' : 'small' }, { key: sum % 2 === 0 ? 'even' : 'odd' }] };
  }
  const pocket = Number(draw.roulette);
  if (!Number.isFinite(pocket) || draw.roulette == null) return null;
  const tone = rouletteColor(pocket);
  const tags: DrawTag[] = [{ key: tone }];
  if (pocket !== 0) {
    tags.push({ key: pocket % 2 === 0 ? 'even' : 'odd' });
    tags.push({ text: pocket >= 19 ? '19–36' : '1–18' });
  }
  return { game, pocket, tone, tags };
}

export function netByCurrency(bets: CardBet[]): Record<string, number> {
  const out: Record<string, number> = {};
  for (const bet of bets) {
    const net = betNetAmount(bet) ?? 0;
    out[bet.currency] = (out[bet.currency] || 0) + net;
  }
  return out;
}

function isGameCode(value: string): value is GameCode {
  return value === 'lastdigit' || value === 'sicbo' || value === 'roulette';
}

function celebrationFor(layout: 'win' | 'loss', bets: CardBet[], fiatCurrency: string, nets: Record<string, number>): CelebrationTier {
  if (layout !== 'win') return 'none';
  const fiatPayout = bets.filter((bet) => bet.currency === fiatCurrency && bet.result === 'win').reduce((sum, bet) => sum + bet.payout, 0);
  const usdtPayout = bets.filter((bet) => bet.currency === 'USDT' && bet.result === 'win').reduce((sum, bet) => sum + bet.payout, 0);
  const fiatNet = nets[fiatCurrency] || 0;
  const usdtNet = nets.USDT || 0;
  if (fiatNet >= 1000) return 'mega';
  if (fiatPayout >= 100 || fiatNet >= 50 || usdtPayout >= 1 || usdtNet >= 1) return 'win';
  return 'small';
}

export function buildResultCard(input: {
  bets: CardBet[];
  draw: CardDraw | null | undefined;
  roomGame: GameCode;
  fiatCurrency: string;
  expanded?: boolean;
}): ResultCardView {
  const { bets, draw, roomGame, fiatCurrency, expanded = false } = input;
  const byCurrency = netByCurrency(bets);
  const currencies = Object.keys(byCurrency).sort((a, b) => (a === fiatCurrency ? -1 : b === fiatCurrency ? 1 : a.localeCompare(b)));
  const nets = currencies.map((currency) => ({ currency, net: byCurrency[currency] }));
  const anyNegative = nets.some((item) => item.net < -0.005);
  const anyPositive = nets.some((item) => item.net > 0.005);
  const layout: 'win' | 'loss' = !anyNegative && anyPositive ? 'win' : 'loss';
  const breakEven = bets.length > 0 && !anyNegative && !anyPositive;

  const betGame = bets.map((bet) => bet.game).find(isGameCode) || roomGame;
  const hero = heroDrawFor(betGame, draw);
  const others = GAME_ORDER.filter((game) => game !== betGame)
    .map((game) => heroDrawFor(game, draw))
    .filter((item): item is HeroDraw => item !== null);

  const allRows: ResultRow[] = bets.map((bet) => ({
    id: bet.id,
    game: bet.game,
    tab: bet.tab,
    pick: bet.pick,
    currency: bet.currency,
    result: bet.result,
    net: betNetAmount(bet) ?? 0,
  }));
  const rows = expanded ? allRows : allRows.slice(0, MAX_ROWS_COLLAPSED);

  return {
    layout,
    breakEven,
    nets,
    netByCurrency: byCurrency,
    hero,
    others,
    rows,
    hiddenRows: allRows.length - rows.length,
    celebration: celebrationFor(layout, bets, fiatCurrency, byCurrency),
    autoCloseMs: layout === 'win' ? WIN_AUTO_CLOSE_MS : LOSS_AUTO_CLOSE_MS,
  };
}
